import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceDto, SetDeviceServiceStateInput } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * Out-of-service toggle (wave 3 #5). `PATCH /devices/:id/service-state`
 * (`devices.manage`) has existed since wave 1 with no UI anywhere — this
 * dialog is its first and only home. Taking a machine out asks for the reason
 * staff will read on the tile; bringing it back is a plain confirm.
 */
export function ServiceStateDialog({
  device,
  onClose,
  onSaved,
}: {
  device: DeviceDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const takingOut = device.status !== 'OUT_OF_SERVICE';

  const [reason, setReason] = useState(device.outOfServiceReason ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleConfirm(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      const payload: SetDeviceServiceStateInput = takingOut
        ? { outOfService: true, reason: reason.trim() || null }
        : { outOfService: false };
      await api.patch(`/devices/${device.id}/service-state`, payload);
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('deviceBoard.serviceDialog.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  const title = takingOut
    ? t('deviceBoard.serviceDialog.outTitle', { device: device.name })
    : t('deviceBoard.serviceDialog.backTitle', { device: device.name });

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-sm rounded-card border border-subtle bg-raised p-5 shadow-2xl">
        <h2 className="text-lg font-semibold text-content">{title}</h2>
        <p className="mt-2 text-sm text-content-secondary">
          {takingOut
            ? t('deviceBoard.serviceDialog.outMessage')
            : t('deviceBoard.serviceDialog.backMessage')}
        </p>

        {takingOut ? (
          <label className="mt-3 block">
            <span className="mb-1 block text-xs text-content-secondary">
              {t('deviceBoard.serviceDialog.reason')}
            </span>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={200}
              placeholder={t('deviceBoard.serviceDialog.reasonPlaceholder')}
              className="w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none placeholder:text-content-muted focus:border-primary"
            />
          </label>
        ) : null}

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={submitting}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {submitting ? t('common.loading') : t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
